import { useEffect, useState, useCallback } from 'react';
import api from '../../lib/api';
import { toast } from 'sonner';
import {
  CreditCard, RefreshCw, TrendingUp, AlertTriangle, Calendar,
  ChevronRight, Star, ArrowUp, ArrowDown, Printer, FileSpreadsheet,
} from 'lucide-react';
import { exportToCSV, printTable } from '../../lib/printUtils';

const KPI_CARD_COLORS = {
  indigo: 'bg-indigo-50 border-indigo-200 text-indigo-700',
  emerald: 'bg-emerald-50 border-emerald-200 text-emerald-700',
  amber: 'bg-amber-50 border-amber-200 text-amber-700',
};
const VENC_BADGE_COLORS = {
  red: 'bg-red-100 text-red-700',
  amber: 'bg-amber-100 text-amber-700',
  gray: 'bg-gray-100 text-gray-600',
};
const CUPO_BAR_COLORS = {
  red: 'bg-red-500',
  amber: 'bg-amber-500',
  emerald: 'bg-emerald-500',
  gray: 'bg-gray-400',
};
const CUPO_TEXT_COLORS = {
  red: 'text-red-700',
  amber: 'text-amber-700',
  emerald: 'text-emerald-700',
  gray: 'text-gray-700',
};

const fmt = (n) => Math.round(n || 0).toLocaleString('es-CL');
const fmtPct = (p) => p == null ? '—' : `${p.toFixed(1)}%`;

function dteldyn(dias) {
  if (dias == null) return null;
  if (dias < 0) return { color: 'red', label: `Vencida hace ${Math.abs(dias)}d` };
  if (dias <= 30) return { color: 'red', label: `Vence en ${dias}d` };
  if (dias <= 90) return { color: 'amber', label: `Vence en ${dias}d` };
  return { color: 'gray', label: `${dias}d para vencer` };
}

export default function TarjetasReportePage() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [expanded, setExpanded] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get('/tarjetas-pago/reporte');
      setData(res.data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Error cargando reporte');
    }
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  // Carlos (09/05/2026): exportar/imprimir reporte de tarjetas
  const imprimirReporte = () => {
    if (!data) return;
    printTable({
      title: 'Reporte por Tarjeta de Pago',
      subtitle: `${data.tarjetas?.length || 0} tarjetas · Generado ${new Date().toLocaleString('es-CL')}`,
      summary: [
        { label: 'Tarjetas activas', value: data.totales?.tarjetas_activas ?? 0 },
        { label: 'Cupo total', value: `$${fmt(data.totales?.cupo_total)}`, color: '#1d4ed8' },
        { label: 'Consumo mes', value: `$${fmt(data.totales?.consumo_mes)}`, color: '#b91c1c' },
        { label: 'Disponible global', value: `$${fmt(data.totales?.disponible_global)}`, color: '#047857', highlight: true },
      ],
      columns: [
        { key: 'nombre', label: 'Tarjeta', format: (v, r) => `${r.favorita ? '⭐ ' : ''}${v || ''} (${r.marca || ''})` },
        { key: 'banco', label: 'Banco' },
        { key: 'titular', label: 'Titular' },
        { key: 'cupo_total', label: 'Cupo', align: 'right', mono: true, format: (v) => `$${fmt(v)}` },
        { key: 'consumido_total', label: 'Consumo total', align: 'right', mono: true, format: (v) => `$${fmt(v)}` },
        { key: 'consumo_mes', label: 'Consumo mes', align: 'right', mono: true, format: (v) => `$${fmt(v)}` },
        { key: 'disponible', label: 'Disponible', align: 'right', mono: true, format: (v) => `$${fmt(v)}` },
        { key: 'pct_uso', label: '% Uso', align: 'center', format: (v) => fmtPct(v) },
        { key: 'dias_para_vencer', label: 'Vencimiento', align: 'center', format: (v) => v != null ? `${v}d` : '-' },
      ],
      rows: data.tarjetas || [],
    });
  };

  const exportarExcel = () => {
    if (!data) return;
    exportToCSV({
      filename: 'reporte_tarjetas',
      title: 'CHIRINO STEEL · Reporte por Tarjeta de Pago',
      summary: [
        { label: 'Tarjetas activas', value: data.totales?.tarjetas_activas ?? 0 },
        { label: 'Cupo total (CLP)', value: Math.round(data.totales?.cupo_total || 0) },
        { label: 'Consumo total (CLP)', value: Math.round(data.totales?.consumido_total || 0) },
        { label: 'Consumo mes (CLP)', value: Math.round(data.totales?.consumo_mes || 0) },
        { label: 'Disponible global (CLP)', value: Math.round(data.totales?.disponible_global || 0) },
        { label: 'Generado', value: new Date().toLocaleString('es-CL') },
      ],
      columns: [
        { key: 'nombre', label: 'Tarjeta' },
        { key: 'marca', label: 'Marca' },
        { key: 'banco', label: 'Banco' },
        { key: 'titular', label: 'Titular' },
        { key: 'favorita', label: 'Favorita', format: (v) => v ? 'SÍ' : '' },
        { key: 'cupo_total', label: 'Cupo (CLP)', format: (v) => Math.round(Number(v) || 0) },
        { key: 'consumido_total', label: 'Consumido total (CLP)', format: (v) => Math.round(Number(v) || 0) },
        { key: 'consumo_mes', label: 'Consumo mes (CLP)', format: (v) => Math.round(Number(v) || 0) },
        { key: 'consumo_mes_count', label: 'Cant. OCs mes' },
        { key: 'disponible', label: 'Disponible (CLP)', format: (v) => Math.round(Number(v) || 0) },
        { key: 'pct_uso', label: '% Uso', format: (v) => v != null ? `${v.toFixed(1)}%` : '' },
        { key: 'dias_para_vencer', label: 'Días vencimiento' },
        { key: 'fecha_vencimiento', label: 'Fecha vencimiento' },
      ],
      rows: data.tarjetas || [],
    });
  };

  if (!data && !loading) return <div className="p-6 text-gray-500">Sin datos.</div>;

  return (
    <div className="p-6 max-w-[1400px] mx-auto" data-testid="tarjetas-reporte-page">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <CreditCard className="w-6 h-6 text-indigo-600" />
            Reporte por Tarjeta de Pago
          </h1>
          <p className="text-sm text-gray-500 mt-1">
            Consumo de OCs Express por tarjeta, mes en curso y comparativo histórico.
          </p>
        </div>
        <div className="flex items-center gap-2">
          {data && (
            <>
              <button
                onClick={imprimirReporte}
                className="flex items-center gap-1.5 px-3 py-1.5 border border-blue-300 text-blue-700 rounded text-sm hover:bg-blue-50"
                title="Imprimir reporte"
                data-testid="btn-print-reporte-tarjetas"
              >
                <Printer className="w-4 h-4" /> Imprimir
              </button>
              <button
                onClick={exportarExcel}
                className="flex items-center gap-1.5 px-3 py-1.5 border border-emerald-300 text-emerald-700 rounded text-sm hover:bg-emerald-50"
                title="Exportar a Excel/CSV"
                data-testid="btn-excel-reporte-tarjetas"
              >
                <FileSpreadsheet className="w-4 h-4" /> Excel
              </button>
            </>
          )}
          <button
            onClick={load}
            disabled={loading}
            className="flex items-center gap-1.5 px-3 py-1.5 bg-indigo-600 text-white rounded text-sm hover:bg-indigo-700 disabled:opacity-50"
            data-testid="btn-refresh-reporte"
          >
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
            Actualizar
          </button>
        </div>
      </div>

      {data && (
        <>
          {/* Totales globales */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-5">
            <KPICard label="Tarjetas activas" value={data.totales.tarjetas_activas} color="indigo" testId="kpi-tarjetas-activas" />
            <KPICard label={`OCs mes actual (${data.periodos.mes_actual_inicio})`} value={data.totales.mes_actual_count} color="emerald" testId="kpi-ocs-mes" />
            <KPICard label="Monto mes actual" value={`$${fmt(data.totales.mes_actual_monto)}`} color="amber" testId="kpi-monto-mes" />
          </div>

          {/* Cards de tarjetas */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {data.tarjetas.map((t) => (
              <TarjetaCard
                key={t.codigo}
                t={t}
                isExpanded={expanded === t.codigo}
                onToggle={() => setExpanded(expanded === t.codigo ? null : t.codigo)}
              />
            ))}
          </div>

          <div className="text-xs text-gray-400 mt-6 text-right">
            Última actualización: {(data.checked_at || '').replace('T', ' ').slice(0, 19)} UTC
          </div>
        </>
      )}
    </div>
  );
}

function KPICard({ label, value, color, testId }) {
  const cls = KPI_CARD_COLORS[color] || KPI_CARD_COLORS.indigo;
  return (
    <div className={`p-4 rounded-lg border ${cls}`} data-testid={testId}>
      <div className="text-2xl font-bold">{value}</div>
      <div className="text-xs text-gray-600 mt-1">{label}</div>
    </div>
  );
}

function TarjetaCard({ t, isExpanded, onToggle }) {
  const k = t.kpis;
  const venc = dteldyn(t.dias_hasta_vencimiento);
  const trendCount = k.mes_actual.count - k.mes_anterior.count;
  const trendMonto = k.mes_actual.monto - k.mes_anterior.monto;
  const isUsada = k.mes_actual.count > 0;

  const cupoColor =
    t.pct_uso_cupo == null ? 'gray' :
    t.pct_uso_cupo >= 90 ? 'red' :
    t.pct_uso_cupo >= 70 ? 'amber' : 'emerald';

  return (
    <div
      className={`border rounded-lg overflow-hidden transition-all ${
        isUsada ? 'border-indigo-300 shadow-sm' : 'border-gray-200'
      }`}
      data-testid={`card-tarjeta-${t.codigo}`}
    >
      {/* Header */}
      <div className={`px-4 py-3 ${isUsada ? 'bg-indigo-50/70' : 'bg-gray-50'} border-b`}>
        <div className="flex items-start justify-between gap-2">
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-1.5">
              {t.favorita && <Star className="w-3.5 h-3.5 text-amber-500 fill-amber-400" />}
              <span className="font-semibold text-sm truncate">{t.etiqueta}</span>
              <span className="text-[10px] uppercase tracking-wide bg-gray-100 px-1.5 py-0.5 rounded text-gray-600">{t.codigo}</span>
            </div>
            <div className="text-xs text-gray-500 mt-0.5 truncate">
              {t.banco || '—'}{t.ultimos_4 ? ` ****${t.ultimos_4}` : ''}{t.marca ? ` · ${t.marca}` : ''}
            </div>
          </div>
          {venc && (
            <span className={`text-[10px] px-2 py-0.5 rounded whitespace-nowrap flex items-center gap-1 ${VENC_BADGE_COLORS[venc.color] || VENC_BADGE_COLORS.gray}`}>
              <Calendar className="w-3 h-3" />
              {venc.label}
            </span>
          )}
        </div>
      </div>

      {/* Body: KPIs */}
      <div className="p-4 space-y-3">
        <div className="grid grid-cols-2 gap-3">
          <div>
            <div className="text-[11px] text-gray-500 uppercase tracking-wide">Mes actual</div>
            <div className="font-bold text-base">${fmt(k.mes_actual.monto)}</div>
            <div className="text-xs text-gray-500">{k.mes_actual.count} OCs</div>
          </div>
          <div>
            <div className="text-[11px] text-gray-500 uppercase tracking-wide flex items-center gap-1">
              vs Mes anterior
              {trendMonto > 0 && <ArrowUp className="w-3 h-3 text-red-600" />}
              {trendMonto < 0 && <ArrowDown className="w-3 h-3 text-emerald-600" />}
            </div>
            <div className="text-xs text-gray-700">${fmt(k.mes_anterior.monto)}</div>
            <div className="text-xs text-gray-500">{k.mes_anterior.count} OCs ({trendCount >= 0 ? '+' : ''}{trendCount})</div>
          </div>
        </div>

        {/* Cupo / uso */}
        {t.cupo && (
          <div>
            <div className="flex items-center justify-between text-[11px] mb-1">
              <span className="text-gray-500">Uso del cupo (mes)</span>
              <span className={`font-semibold ${CUPO_TEXT_COLORS[cupoColor]}`}>{fmtPct(t.pct_uso_cupo)} de ${fmt(t.cupo)}</span>
            </div>
            <div className="h-1.5 bg-gray-200 rounded-full overflow-hidden">
              <div
                className={`h-full transition-all ${CUPO_BAR_COLORS[cupoColor]}`}
                style={{ width: `${Math.min(100, t.pct_uso_cupo || 0)}%` }}
              />
            </div>
          </div>
        )}

        {/* Año / total */}
        <div className="grid grid-cols-2 gap-3 pt-2 border-t border-gray-100">
          <div className="text-xs">
            <span className="text-gray-500">Año en curso:</span>{' '}
            <span className="font-medium">{k.anio_actual.count} OCs · ${fmt(k.anio_actual.monto)}</span>
          </div>
          <div className="text-xs">
            <span className="text-gray-500">Histórico:</span>{' '}
            <span className="font-medium">{k.total_historico.count} OCs</span>
          </div>
        </div>

        {/* Toggle: ver últimas */}
        {t.ocs_recientes?.length > 0 && (
          <>
            <button
              onClick={onToggle}
              className="text-xs text-indigo-600 hover:underline flex items-center gap-1 font-medium"
              data-testid={`btn-toggle-recientes-${t.codigo}`}
            >
              <ChevronRight className={`w-3 h-3 transition-transform ${isExpanded ? 'rotate-90' : ''}`} />
              {isExpanded ? 'Ocultar' : 'Ver'} últimas {t.ocs_recientes.length} OCs
            </button>
            {isExpanded && (
              <div className="space-y-1 pt-2 border-t border-gray-100" data-testid={`recientes-${t.codigo}`}>
                {t.ocs_recientes.map((oc, idx) => (
                  <div key={idx} className="flex items-center justify-between text-xs py-1 border-b border-gray-50 last:border-0">
                    <div className="min-w-0 flex-1">
                      <span className="font-mono font-semibold">OC {oc.numero_oc}</span>
                      <span className="text-gray-500 ml-2 truncate">{oc.razon_social_proveedor || oc.rut_proveedor}</span>
                    </div>
                    <div className="text-right whitespace-nowrap ml-2">
                      <div className="font-semibold">${fmt(oc.neto)}</div>
                      <div className="text-[10px] text-gray-400">{(oc.fecha_creacion || '').slice(0, 10)}</div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </>
        )}

        {!isUsada && (
          <div className="text-xs text-gray-400 italic flex items-center gap-1 pt-1 border-t border-gray-100">
            <TrendingUp className="w-3 h-3" />
            Sin movimientos en el mes actual
          </div>
        )}
      </div>
    </div>
  );
}
